import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MdArrowBack,
  MdDownload,
  MdVisibility,
  MdLocalOffer,
  MdLocalShipping,
  MdReceipt,
  MdCheckCircle,
  MdInfo,
  MdAssignment,
  MdEdit,
} from 'react-icons/md';
import { canEditAdminOffer } from '../../utils/admin-offer-edit';
import {
  getOfferResponseLabel,
  isOfferAcceptedLike,
  offerResponseBadgeClass,
} from '../../utils/offer-response-status';
import { EUROASIANN_OFFER_LABEL } from '@euroasiann/shared';
import { authenticatedFetch } from '../../lib/api';
import { cn } from '../../lib/utils';
import { useToast } from '../../components/shared/Toast';
import { downloadAdminOfferPDF, viewAdminOfferPDF } from '../../utils/offer-pdf';
import { AdminShippingChargesSection } from '../../components/orders/AdminShippingChargesSection';
import { AdminInvoicesSection } from '../../components/orders/AdminInvoicesSection';
import { PageSkeleton } from '@euroasiann/shared/ui';

type AdminOfferDetail = {
  _id: string;
  quotationNumber: string;
  title: string;
  description?: string;
  status: string;
  totalAmount: number;
  currency: string;
  createdAt: string;
  items?: Array<Record<string, unknown>>;
  responseStatus?: 'pending' | 'accepted' | 'partially_accepted' | 'rejected';
  terms?: Record<string, string | number | undefined>;
  metadata?: Record<string, any>;
  linkedPurchaseOrder?: {
    _id: string;
    poNumber?: string;
    status: string;
    shippingMode?: 'vendor' | 'self' | null;
    shippingChargesConfigured?: boolean;
    proformaInvoiceSent?: boolean;
    proformaInvoiceApproved?: boolean;
    finalInvoiceIssued?: boolean;
    paymentUnlocked?: boolean;
  };
};

function workflowLabel(offer: AdminOfferDetail, purchaseOrder?: any | null): string {
  const meta = offer.metadata || {};
  const po = purchaseOrder || offer.linkedPurchaseOrder;
  const accepted = isOfferAcceptedLike(offer);
  if (!accepted) return 'Awaiting customer response';
  if (!po && meta.guestAccepted) {
    return 'Accepted — select customer organization below to generate the purchase order';
  }
  if (!po) return 'Generating purchase order and sending for signature…';

  const sigStatus = po.metadata?.signatureStatus || po.signatureStatus;
  if (sigStatus === 'PENDING' || sigStatus === 'IN_PROGRESS') {
    return 'PO issued — awaiting customer signature via Documenso';
  }
  if (sigStatus === 'REJECTED') return 'PO signature declined — follow up with customer';
  if (sigStatus === 'SIGNED' || sigStatus === 'NOT_REQUESTED' || !sigStatus) {
    // fall through to fulfillment stages
  }

  if (po.paymentUnlocked || po.finalInvoiceIssued) return 'Final invoice issued — payment unlocked';
  if (po.proformaInvoiceApproved) {
    if (po.shippingMode === 'self') return 'Proforma approved — self shipment confirmed';
    if (meta.adminShippingRequested || po.metadata?.adminShippingRequested) {
      if (!po.shippingChargesConfigured) return 'Customer asked for shipping charges — add them below';
      return 'Shipping charges set — issue final invoice';
    }
    if (!po.shippingMode) return 'Proforma approved — await customer shipping choice';
    if (po.shippingMode === 'vendor' && !po.shippingChargesConfigured) {
      return 'Configure shipping charges, then issue final invoice';
    }
    return 'Issue final invoice (includes shipping if any)';
  }
  if (po.proformaInvoiceSent) return 'Proforma sent — awaiting customer approval';
  if (sigStatus === 'SIGNED') return 'PO signed — send proforma invoice (item prices only)';
  return 'Send proforma invoice (item prices only)';
}

export function OfferDetailsPage({
  embedded = false,
  offerId: offerIdProp,
  backTo = '/dashboard/admin/offers',
}: {
  embedded?: boolean;
  offerId?: string;
  backTo?: string;
} = {}) {
  const { id: idFromParams } = useParams<{ id: string }>();
  const id = offerIdProp || idFromParams;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const goBack = () => navigate(backTo);
  const goToEdit = () => {
    if (embedded && id) {
      navigate(
        `${backTo}${backTo.includes('?') ? '&' : '?'}offerView=edit&offerId=${id}`
      );
      return;
    }
    if (id) navigate(`/dashboard/admin/offers/${id}/edit`);
  };
  const [customerOrgId, setCustomerOrgId] = useState('');
  const autoEnsureStarted = useRef(false);
  const [shippingPrefill, setShippingPrefill] = useState<{
    charges: Array<{ label: string; amount: string; chargeType: 'shipping' | 'packing' | 'custom' | 'other' }>;
    taxes: Array<{ label: string; rate: string; amount: string }>;
    notes?: string;
  } | null>(null);
  const [customerTinDraft, setCustomerTinDraft] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-offer-detail', id],
    queryFn: async () => {
      if (!id) throw new Error('Offer id required');
      const res = await authenticatedFetch(`/api/v1/admin/quotation/${id}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to load offer');
      }
      const json = await res.json();
      return json.data as { offer: AdminOfferDetail; purchaseOrder: any | null };
    },
    enabled: !!id,
  });

  const { data: customerOrgs = [] } = useQuery({
    queryKey: ['admin-customer-orgs-picker'],
    queryFn: async () => {
      const res = await authenticatedFetch('/api/v1/admin/organizations?portalType=customer&limit=200');
      if (!res.ok) return [];
      const json = await res.json();
      const raw = json.data?.organizations ?? json.data?.data ?? json.data ?? json.organizations ?? [];
      return Array.isArray(raw) ? raw.filter((o: any) => o.portalType === 'customer' || o.type === 'customer') : [];
    },
    enabled: !!data?.offer && !data?.purchaseOrder,
  });

  const offer = data?.offer;
  const po = data?.purchaseOrder;
  const poId = po?._id || offer?.linkedPurchaseOrder?._id || offer?.metadata?.purchaseOrderId;

  const { data: linkedShipment } = useQuery({
    queryKey: ['admin-offer-shipment', po?.poNumber],
    queryFn: async () => {
      if (!po?.poNumber) return null;
      const res = await authenticatedFetch(
        `/api/v1/admin/shipments?search=${encodeURIComponent(po.poNumber)}`
      );
      if (!res.ok) return null;
      const json = await res.json();
      const list = json.data?.shipments || [];
      return list.find((s: any) => s.orderNumber === po.poNumber) || list[0] || null;
    },
    enabled: !!po?.poNumber,
  });

  const ensureOrderMutation = useMutation({
    mutationFn: async () => {
      const res = await authenticatedFetch(`/api/v1/admin/quotation/${id}/ensure-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerOrganizationId: customerOrgId || undefined,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to create order');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-offer-detail', id] });
      queryClient.invalidateQueries({ queryKey: ['admin-offers'] });
      showToast('Purchase order generated and sent for signature', 'success');
    },
    onError: (e: Error) => showToast(e.message, 'error'),
  });

  const meta = offer?.metadata || {};
  const responseLabelText = offer ? getOfferResponseLabel(offer) : 'Pending';
  const accepted = offer ? isOfferAcceptedLike(offer) : false;
  const rejected = responseLabelText === 'Rejected';
  const showFulfillment = accepted && !!po && !!poId;
  const poEnsureFailed = accepted && !po && ensureOrderMutation.isError;

  useEffect(() => {
    if (offer?.metadata?.customerOrganizationId && !customerOrgId) {
      setCustomerOrgId(String(offer.metadata.customerOrganizationId));
    }
  }, [offer?.metadata?.customerOrganizationId, customerOrgId]);

  useEffect(() => {
    if (!accepted || po || autoEnsureStarted.current || !id) return;
    autoEnsureStarted.current = true;
    ensureOrderMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once when accepted without PO
  }, [accepted, po, id]);

  useEffect(() => {
    const tin = meta.customerTin || meta.extractedTaxId || '';
    if (tin && !customerTinDraft) setCustomerTinDraft(String(tin));
  }, [meta.customerTin, meta.extractedTaxId, customerTinDraft]);

  useEffect(() => {
    if (!showFulfillment) return;
    const highlight =
      meta.adminShippingRequested || po?.metadata?.adminShippingRequested;
    if (highlight || window.location.hash === '#shipping-charges') {
      window.setTimeout(() => {
        document.getElementById('shipping-charges')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [showFulfillment, meta.adminShippingRequested, po?.metadata?.adminShippingRequested]);

  const saveTinMutation = useMutation({
    mutationFn: async () => {
      const res = await authenticatedFetch(`/api/v1/admin/quotation/${id}/customer-tin`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerTin: customerTinDraft.trim() || undefined }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to save tax ID');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-offer-detail', id] });
      showToast('Tax identification updated', 'success');
    },
    onError: (e: Error) => showToast(e.message, 'error'),
  });

  const displayItems = useMemo(() => {
    if (!offer) return [];
    const raw = (offer.metadata?.items as any[]) || offer.items || [];
    return Array.isArray(raw) ? raw : [];
  }, [offer]);

  if (isLoading) {
    return <PageSkeleton variant="cards" />;
  }

  if (error || !offer) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600 font-semibold">{(error as Error)?.message || 'Offer not found'}</p>
        <button
          type="button"
          onClick={goBack}
          className="mt-4 px-4 py-2 rounded-xl bg-[#0088cc] text-white font-bold"
        >
          Back to Offers
        </button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'w-full max-w-7xl mx-auto space-y-5',
        embedded ? 'p-0' : 'p-4 lg:p-6'
      )}
    >
      {/* Top Navigation & Breadcrumb */}
      <div className="flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-[#0088cc] text-sm font-semibold transition-colors"
        >
          <MdArrowBack className="w-4 h-4" /> Back to Offers
        </button>
        <span className="text-[11px] font-black uppercase tracking-[0.2em] text-[#0088cc]">
          {EUROASIANN_OFFER_LABEL}
        </span>
      </div>

      {/* Header Banner & Primary Actions */}
      <div className="bg-card rounded-2xl border p-5 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1 className="text-2xl lg:text-3xl font-black text-[hsl(var(--foreground))]">
                {offer.quotationNumber}
              </h1>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 dark:bg-blue-950/40 text-blue-800 dark:text-blue-300 capitalize">
                {offer.status}
              </span>
              <span
                className={cn(
                  'px-2.5 py-0.5 rounded-full text-xs font-bold',
                  offerResponseBadgeClass(responseLabelText)
                )}
              >
                {responseLabelText}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">{offer.title || 'Commercial spare parts offer'}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {canEditAdminOffer(offer) && (
              <button
                type="button"
                onClick={goToEdit}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-amber-500/40 bg-amber-50/50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-200 text-xs font-bold hover:bg-amber-100/60 transition"
              >
                <MdEdit className="w-4 h-4" /> Edit & Resend
              </button>
            )}
            <button
              type="button"
              onClick={() => void viewAdminOfferPDF(offer)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border font-bold text-xs hover:bg-muted transition"
            >
              <MdVisibility className="w-4 h-4 text-[#0088cc]" /> View PDF
            </button>
            <button
              type="button"
              onClick={() => void downloadAdminOfferPDF(offer)}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#0088cc] text-white font-bold text-xs hover:bg-[#0077b3] shadow-sm transition"
            >
              <MdDownload className="w-4 h-4" /> Download PDF
            </button>
          </div>
        </div>
      </div>

      {/* 4 KPI Top Stat Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card rounded-2xl border p-4 shadow-sm flex flex-col justify-between">
          <p className="text-[11px] font-black uppercase tracking-wider text-muted-foreground">Total Value</p>
          <div className="mt-2">
            <p className="text-2xl font-black text-primary">
              {offer.currency} {Number(offer.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {displayItems.length} line {displayItems.length === 1 ? 'item' : 'items'}
            </p>
          </div>
        </div>

        <div className="bg-card rounded-2xl border p-4 shadow-sm flex flex-col justify-between">
          <p className="text-[11px] font-black uppercase tracking-wider text-muted-foreground">Recipient</p>
          <div className="mt-2">
            <p className="text-base font-bold truncate text-[hsl(var(--foreground))]">{meta.recipientName || '—'}</p>
            <p className="text-xs text-muted-foreground truncate mt-0.5">{meta.recipientEmail || '—'}</p>
          </div>
        </div>

        <div className="bg-card rounded-2xl border p-4 shadow-sm flex flex-col justify-between">
          <p className="text-[11px] font-black uppercase tracking-wider text-muted-foreground">Workflow Stage</p>
          <div className="mt-2">
            <p className="text-xs font-bold text-[#0088cc] line-clamp-2">{workflowLabel(offer, po)}</p>
            <p className="text-[11px] text-muted-foreground mt-1">
              {offer.createdAt ? `Created ${new Date(offer.createdAt).toLocaleDateString()}` : '—'}
            </p>
          </div>
        </div>

        <div className="bg-card rounded-2xl border p-4 shadow-sm flex flex-col justify-between">
          <p className="text-[11px] font-black uppercase tracking-wider text-muted-foreground">Purchase Order</p>
          <div className="mt-2">
            {po?.poNumber || meta.purchaseOrderNumber ? (
              <>
                <p className="text-sm font-mono font-bold text-[#0088cc]">
                  {po?.poNumber || meta.purchaseOrderNumber}
                </p>
                <p className="text-xs text-muted-foreground capitalize mt-0.5">
                  Status: <span className="font-semibold">{po?.status || 'Active'}</span>
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground italic">Not created yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Main Grid Layout: Left Content (2 cols) & Right Sidebar (1 col) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        {/* Left Column (2 Cols) */}
        <div className="lg:col-span-2 space-y-5">
          {/* Line Items Table Card */}
          <div className="bg-card rounded-2xl border overflow-hidden shadow-sm">
            <div className="px-5 py-4 border-b bg-muted/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MdAssignment className="w-5 h-5 text-[#0088cc]" />
                <h2 className="text-base font-black text-[hsl(var(--foreground))]">Line Items ({displayItems.length})</h2>
              </div>
              <span className="text-xs font-semibold text-muted-foreground">Currency: {offer.currency}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-[10px] uppercase font-black tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left py-3 px-4">#</th>
                    <th className="text-left py-3 px-4">Description</th>
                    <th className="text-center py-3 px-4">Qty</th>
                    <th className="text-right py-3 px-4">Unit Price</th>
                    <th className="text-right py-3 px-4">Line Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/60">
                  {displayItems.length > 0 ? (
                    displayItems.map((item: any, idx: number) => {
                      const qty = Number(item.offeredQty || item.requiredQty || item.quantity || 0);
                      const price = Number(item.quotedPrice || item.unitPrice || 0);
                      const total = Number(item.lineTotal ?? qty * price);
                      return (
                        <tr key={idx} className="hover:bg-muted/10 transition-colors">
                          <td className="py-3.5 px-4 text-xs font-mono text-muted-foreground">{idx + 1}</td>
                          <td className="py-3.5 px-4">
                            <p className="font-semibold text-[hsl(var(--foreground))]">{item.description || item.itemDescription || '—'}</p>
                            {(item.partNo || item.impaNo) && (
                              <p className="text-[11px] text-muted-foreground mt-0.5">
                                {[item.partNo ? `Part: ${item.partNo}` : null, item.impaNo ? `IMPA: ${item.impaNo}` : null]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </p>
                            )}
                          </td>
                          <td className="py-3.5 px-4 text-center font-medium">
                            {qty} {item.uom || ''}
                          </td>
                          <td className="py-3.5 px-4 text-right font-medium">
                            {offer.currency} {price.toFixed(2)}
                          </td>
                          <td className="py-3.5 px-4 text-right font-bold text-[hsl(var(--foreground))]">
                            {offer.currency} {total.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-muted-foreground italic">No line items</td>
                    </tr>
                  )}
                </tbody>
                <tfoot className="border-t bg-muted/20 text-sm font-bold">
                  {offer.metadata?.taxConfig?.enabled && (Number(offer.metadata.taxConfig.taxRate) > 0 || Number(offer.metadata.taxConfig.taxAmount) > 0) && (
                    <>
                      <tr>
                        <td colSpan={4} className="py-2 px-4 text-right text-xs text-muted-foreground font-semibold">Subtotal:</td>
                        <td className="py-2 px-4 text-right text-xs font-semibold">
                          {offer.currency} {Number(offer.metadata.taxConfig.subtotal || (Number(offer.totalAmount) - Number(offer.metadata.taxConfig.taxAmount || 0))).toFixed(2)}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={4} className="py-2 px-4 text-right text-xs text-muted-foreground font-semibold">
                          Tax ({offer.metadata.taxConfig.taxRate}%):
                        </td>
                        <td className="py-2 px-4 text-right text-xs text-primary font-semibold">
                          {offer.currency} {Number(offer.metadata.taxConfig.taxAmount || 0).toFixed(2)}
                        </td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <td colSpan={4} className="py-3 px-4 text-right">Grand Total:</td>
                    <td className="py-3 px-4 text-right text-primary font-black">
                      {offer.currency} {Number(offer.totalAmount || 0).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Commercial Terms Card */}
          {offer.terms && Object.keys(offer.terms).length > 0 && (
            <div className="bg-card rounded-2xl border p-5 shadow-sm space-y-3">
              <h2 className="text-sm font-black uppercase tracking-wider text-muted-foreground">Commercial Terms</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {Object.entries(offer.terms).map(([key, val]) =>
                  val ? (
                    <div key={key} className="rounded-xl border border-muted bg-muted/15 p-3">
                      <dt className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        {key.replace(/([A-Z])/g, ' $1')}
                      </dt>
                      <dd className="font-semibold text-xs mt-1 text-[hsl(var(--foreground))]">{String(val)}</dd>
                    </div>
                  ) : null
                )}
              </div>
            </div>
          )}

          {/* PO Creation Banner when accepted without linked PO */}
          {accepted && !po && (
            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40 rounded-2xl p-5 space-y-4">
              <h2 className="text-base font-black flex items-center gap-2 text-amber-900 dark:text-amber-200">
                <MdReceipt className="w-5 h-5 text-amber-600" /> Purchase Order Finalization
              </h2>
              {ensureOrderMutation.isPending && !poEnsureFailed ? (
                <p className="text-sm text-muted-foreground">
                  Generating purchase order for <strong>{meta.recipientEmail || 'recipient'}</strong>, sending Documenso
                  signature to their email and notifying admin…
                </p>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    {meta.guestAccepted || poEnsureFailed
                      ? `Could not link ${meta.recipientEmail || 'recipient email'} to a customer organization automatically. Pick the customer company below — the PO PDF will be emailed to ${meta.recipientEmail || 'the recipient'} for Documenso signature.`
                      : 'Finalizing purchase order…'}
                  </p>
                  <label className="block text-xs font-bold uppercase text-muted-foreground">
                    Customer organization
                    <select
                      value={customerOrgId}
                      onChange={(e) => setCustomerOrgId(e.target.value)}
                      className="mt-1.5 w-full rounded-xl border px-3 py-2.5 text-sm bg-background"
                    >
                      <option value="">Select customer company…</option>
                      {customerOrgs.map((org: any) => (
                        <option key={org._id} value={org._id}>
                          {org.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    disabled={ensureOrderMutation.isPending}
                    onClick={() => ensureOrderMutation.mutate()}
                    className="px-5 py-2.5 rounded-xl bg-[#0088cc] text-white font-bold text-sm hover:bg-[#0077b3] disabled:opacity-50 transition"
                  >
                    {ensureOrderMutation.isPending ? 'Generating…' : 'Generate PO & email for signature'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Fulfillment Lifecycle Sections */}
          {showFulfillment && poId && (
            <div className="space-y-5">
              <AdminInvoicesSection
                po={po}
                poId={poId}
                onExtractedShippingCharges={setShippingPrefill}
              />

              <AdminShippingChargesSection
                po={po}
                poId={poId}
                prefill={shippingPrefill}
                onPrefillApplied={() => setShippingPrefill(null)}
                highlightCustomerRequest={Boolean(
                  meta.adminShippingRequested || po?.metadata?.adminShippingRequested
                )}
              />

              <div className="bg-card rounded-2xl border p-5 shadow-sm space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-black flex items-center gap-2">
                    <MdLocalShipping className="w-5 h-5 text-[#0088cc]" />
                    Tracking & Dispatch
                  </h2>
                  {linkedShipment && (
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#0088cc]/15 text-[#0088cc] uppercase">
                      {linkedShipment.status}
                    </span>
                  )}
                </div>
                {linkedShipment ? (
                  <div className="space-y-2 text-sm">
                    <p className="text-muted-foreground">Tracking Number: <span className="font-mono font-bold text-[hsl(var(--foreground))]">{linkedShipment.trackingNumber || 'Not set'}</span></p>
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/dashboard/admin/shipments?id=${linkedShipment._id}&search=${encodeURIComponent(po.poNumber)}`
                        )
                      }
                      className="px-4 py-2 rounded-xl bg-[#0088cc] text-white text-xs font-bold hover:bg-[#0077b3] transition"
                    >
                      View / edit tracking
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No shipment registered yet. The vendor initializes tracking from the vendor portal
                    after payment is approved.
                  </p>
                )}
              </div>
            </div>
          )}

          {accepted && po && (po.metadata?.paymentUnlocked || po.metadata?.finalInvoiceIssued) && (
            <div className="flex items-center gap-2 p-4 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800/40 rounded-xl text-sm text-emerald-800 dark:text-emerald-300 font-medium">
              <MdCheckCircle className="w-5 h-5 shrink-0 text-emerald-600" />
              Proforma accepted — final invoice issued and payment unlocked.
            </div>
          )}
        </div>

        {/* Right Column / Sidebar (1 Col) */}
        <div className="space-y-5">
          {/* Linked PO & Documenso Signature Card */}
          {accepted && po && (
            <div className="bg-card rounded-2xl border p-5 shadow-sm space-y-3">
              <h3 className="text-xs font-black uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <MdAssignment className="w-4 h-4 text-[#0088cc]" /> Purchase Order & Signature
              </h3>
              <div className="space-y-1.5 text-sm">
                <p className="font-bold text-[hsl(var(--foreground))] font-mono">{po.poNumber || meta.purchaseOrderNumber || '—'}</p>
                <p className="text-xs text-muted-foreground capitalize">Status: <span className="font-semibold">{po.status || '—'}</span></p>
                {po.metadata?.signatureStatus && (
                  <p className="text-xs">
                    <span className="text-muted-foreground">Documenso:</span>{' '}
                    <span className="font-semibold capitalize text-[#0088cc]">
                      {String(po.metadata.signatureStatus).toLowerCase().replace(/_/g, ' ')}
                    </span>
                    {po.metadata.signedAt && (
                      <span className="text-muted-foreground">
                        {' '}
                        · {new Date(po.metadata.signedAt).toLocaleDateString()}
                      </span>
                    )}
                  </p>
                )}
                {po.metadata?.providerSigningUrl && po.metadata?.signatureStatus !== 'SIGNED' && (
                  <a
                    href={po.metadata.providerSigningUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex text-[#0088cc] font-bold text-xs hover:underline pt-1"
                  >
                    Open Documenso Signing Link →
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Delivery Address Card */}
          {accepted && meta.deliveryAddress?.street && (
            <div className="bg-card rounded-2xl border p-5 shadow-sm space-y-2 text-sm">
              <h3 className="text-xs font-black uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <MdLocalShipping className="w-4 h-4 text-[#0088cc]" /> Delivery Address
              </h3>
              <p className="font-semibold text-xs whitespace-pre-wrap text-[hsl(var(--foreground))]">{meta.deliveryAddress.street}</p>
              <p className="text-xs text-muted-foreground">
                {[meta.deliveryAddress.city, meta.deliveryAddress.state, meta.deliveryAddress.country]
                  .filter(Boolean)
                  .join(', ')}
              </p>
              {meta.deliveryAddress.contactName && (
                <p className="text-xs text-muted-foreground pt-1">Contact: {meta.deliveryAddress.contactName}</p>
              )}
            </div>
          )}

          {/* Tax Identification Card */}
          {accepted && (
            <div className="bg-card rounded-2xl border p-5 shadow-sm space-y-3 text-sm">
              <h3 className="text-xs font-black uppercase tracking-wider text-muted-foreground">Tax Identification</h3>
              <div className="space-y-2">
                <input
                  type="text"
                  value={customerTinDraft}
                  onChange={(e) => setCustomerTinDraft(e.target.value.toUpperCase())}
                  placeholder="Customer TIN / VAT / GSTIN"
                  className="w-full rounded-xl border px-3 py-2 text-xs bg-background"
                />
                {meta.taxIdSkipped && !meta.customerTin && (
                  <p className="text-xs text-amber-700 dark:text-amber-300">Customer skipped TIN during acceptance.</p>
                )}
                {meta.poCopyFileName && (
                  <p className="text-xs text-muted-foreground">PO copy: {meta.poCopyFileName}</p>
                )}
                <button
                  type="button"
                  onClick={() => saveTinMutation.mutate()}
                  disabled={saveTinMutation.isPending}
                  className="w-full py-2 rounded-xl bg-[#0088cc] text-white text-xs font-bold hover:bg-[#0077b3] disabled:opacity-50 transition"
                >
                  {saveTinMutation.isPending ? 'Saving…' : 'Save Tax ID'}
                </button>
              </div>
            </div>
          )}

          {/* Shipping Choice Card */}
          {accepted && (
            <div className="bg-card rounded-2xl border p-5 shadow-sm space-y-2 text-sm">
              <h3 className="text-xs font-black uppercase tracking-wider text-muted-foreground">Shipping Mode</h3>
              <p className="font-semibold text-xs capitalize text-[hsl(var(--foreground))]">
                {meta.shippingMode === 'self' ? 'Self Shipment' : meta.shippingMode === 'vendor' ? 'Vendor Shipping' : '—'}
              </p>
              {meta.shippingMode === 'self' && (
                <div className="text-xs text-muted-foreground space-y-1">
                  <p><span className="font-medium">Courier:</span> {meta.courierService || '—'}</p>
                  <p><span className="font-medium">AWB:</span> {meta.awbNumber || '—'}</p>
                </div>
              )}
              {meta.adminShippingRequested && (
                <p className="text-amber-700 dark:text-amber-300 font-semibold text-xs">Customer asked for shipping charges</p>
              )}
            </div>
          )}

          {/* Rejection Alert Card */}
          {rejected && meta.rejectionReason && (
            <div className="bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/40 rounded-2xl p-5 text-sm text-rose-800 dark:text-rose-200">
              <p className="font-bold text-xs uppercase tracking-wider">Rejection Reason</p>
              <p className="mt-2 text-xs">{meta.rejectionReason}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OfferDetailsPage;
